import { Fragment, useState } from 'react';

import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import {
  FaRegCalendar,
  FaRegClock,
  FaLocationDot,
  FaRegUser
} from "react-icons/fa6";
import { IoLanguage } from "react-icons/io5";

import { postDataWithHeaderHelper } from "../requests/PostRequests";

import { CustomDatePicker } from './DatePicker';
import { getLanguage } from "../helpers/LanguageTranslation";
import LoadingIcon from "./LoadingIcon";
import { InputGroup, Table } from 'react-bootstrap';
import { DeleteConfirmation } from "../components/DeleteConfirmation";
import { LanguageDropdown } from "../components/LanguageDropdown";
import { LocationDropdown } from './LocationDropdown';
import { TrainerDropdown } from './TrainerDropdown';
import { ObjectStatus, StatusIcon, updateStatus } from "../components/ObjectStatus";

const defaultClass = {
  status: ObjectStatus.New,
  id: null,
  location_id: null,
  trainer_id: null,
  start_date: new Date(),
  end_date: null,
  number_of_sessions: null,
  language: null,
};

function ClassForm({
  currentClass,
  locations,
  trainers,
  showFlag,
  onFormClose,
  onCreate,
  onUpdate }) {

  const [errorMessage, setErrorMessage] = useState([]);
  const [classForm, setClassForm] = useState(currentClass);
  const onSave = currentClass.id == null ? onCreate : onUpdate;
  const [validated, setValidated] = useState(false);

  function checkValidClassForm(form) {
    let result = [];

    if (form.language == null) {
      result.push("missing language");
    }

    if (form.location_id == null) {
      result.push(" missing location");
    }

    if (form.trainer_id == null) {
      result.push(" missing trainer");
    }

    if (form.start_date > form.end_date) {
      result.push(" start date is later than end date");
    }

    if (result.length > 0) {
      setErrorMessage(result);
      return false;
    }

    return true;
  }

  function onLanguageUpdate(languageCode) {
    classForm.language = languageCode;
    classForm.status = updateStatus(classForm.status, ObjectStatus.Updated);
    setClassForm({ ...classForm });
  }

  function onLocationUpdate(location) {
    classForm.location_id = location.id;
    classForm.location_name = location.name;
    classForm.postal = location.postal;
    classForm.address = location.address;
    classForm.status = updateStatus(classForm.status, ObjectStatus.Updated);
    setClassForm({ ...classForm });
  }

  function onTrainerUpdate(trainer) {
    classForm.trainer_id = trainer.id;
    classForm.trainer_name = trainer.name;
    classForm.status = updateStatus(classForm.status, ObjectStatus.Updated);
    setClassForm({ ...classForm });
  }

  function onStartDateChange(date) {
    classForm.start_date = date;
    classForm.status = updateStatus(classForm.status, ObjectStatus.Updated);
    setClassForm({ ...classForm });
  }

  function onEndDateChange(date) {
    classForm.end_date = date;
    classForm.status = updateStatus(classForm.status, ObjectStatus.Updated);
    setClassForm({ ...classForm });
  }

  function onFormChange(e) {
    classForm.status = updateStatus(classForm.status, ObjectStatus.Updated);
    setClassForm({ ...classForm, [e.target.name]: e.target.value });
  }

  const handleSubmit = (event) => {
    const form = event.currentTarget;
    event.preventDefault();

    if (form.checkValidity() === false || !checkValidClassForm(classForm)) {
      event.stopPropagation();
    } else {
      onSave(classForm);
    }

    setValidated(true);
  };

  return (
    <Modal size="lg" centered backdrop="static" keyboard={false} show={showFlag} onHide={onFormClose}>
      <Modal.Header closeButton>
        <Modal.Title>{classForm.id == null ? "New Class" : `Class ID: ${classForm.id}`} <StatusIcon status={classForm.status} /></Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          {/* Set the language */}
          <Form.Group
            className="mb-3"
            controlId="classForm.language"
          >
            <Form.Label>Language:</Form.Label>
            <LanguageDropdown
              key={`language_dropdown`}
              value={classForm.language}
              translationIndex={classForm.id}
              onUpdate={onLanguageUpdate} />
          </Form.Group>
          {/* Set the trainer */}
          <Form.Group
            className="mb-3"
            controlId="classForm.trainerId"
          >
            <Form.Label>Trainer:</Form.Label>
            <TrainerDropdown
              id={classForm.trainer_id}
              trainers={trainers}
              onUpdate={onTrainerUpdate} />
          </Form.Group>
          {/* Set the location */}
          <Form.Group
            className="mb-3"
            controlId="classForm.location"
          >
            <Form.Label>Location:</Form.Label>
            <LocationDropdown
              id={classForm.location_id}
              locations={locations}
              onUpdate={onLocationUpdate} />
          </Form.Group>
          {/* Set the number of sessions */}
          <Form.Group
            className="mb-3"
            controlId="classForm.numOfSessions"
          >
            <Form.Label>Number of Sessions</Form.Label>
            <InputGroup hasValidation>
              <Form.Control
                required
                name="number_of_sessions"
                defaultValue={classForm.number_of_sessions}
                placeholder="Number of Session"
                onChange={onFormChange} />
              <Form.Control.Feedback type="invalid">
                Please enter the number of sessions.
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
          {/* Set the start and end date date */}
          <Form.Group
            className="mb-3"
            controlId="classForm.dates"
          >
            <Table>
              <tbody>
                <tr>
                  <td>Start Date</td>
                  <td>
                    <CustomDatePicker
                      required
                      key={`class_form_start_date_picker`}
                      className="form-control"
                      selected={classForm.start_date}
                      onChange={onStartDateChange}
                      showTimeSelect
                      dateFormat="Pp" />
                  </td>
                  <td>End Date</td>
                  <td>
                    <CustomDatePicker
                      required
                      key={`class_form_end_date_picker`}
                      className="form-control"
                      selected={classForm.end_date}
                      onChange={onEndDateChange}
                      showTimeSelect
                      dateFormat="Pp" />
                  </td>
                </tr>
              </tbody>
            </Table>
          </Form.Group>
          {/* Show error message if form is not valid */}
          <Alert
            key={`class_form_error_alert`}
            variant='danger'
            show={errorMessage.length > 0}>
            {`Invalid form, please check the following failed conditions:  ${errorMessage}`}
          </Alert>
          <Button type="submit" variant="primary">
            Save Changes
          </Button>
        </Form>
      </Modal.Body >
      {/* User should be able to close or save current course changes */}
      <Modal.Footer>
        <Button variant="secondary" onClick={onFormClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal >
  );
}

const Class = ({ id, cls, index, onEdit, onDelete }) => {
  /*
  NOTE: start_date and end_date values contains class date range (date part) and class timing (time part)
 */
  const startDateFormatted = new Date(cls.start_date).toLocaleDateString(
    "en-sg",
    { year: "numeric", month: "short", day: "numeric" }
  );
  const endDateFormatted = new Date(cls.end_date).toLocaleDateString(
    "en-sg",
    { year: "numeric", month: "short", day: "numeric" }
  );
  const startTimeFormatted = new Date(cls.start_date).toLocaleTimeString(
    "en-us",
    { hour: "2-digit", minute: "2-digit" }
  );
  const endTimeFormatted = new Date(cls.end_date).toLocaleTimeString(
    "en-us",
    { hour: "2-digit", minute: "2-digit" }
  );
  const langFormatted = getLanguage(cls.language);

  return (
    <Fragment key={`${id}_${cls.id}_class_fragment_container`}>
      <tr>
        <td>
          <Card.Subtitle>
            <FaRegCalendar className="class-icn" /> {startDateFormatted}{" "}
            - {endDateFormatted}
          </Card.Subtitle>
          <Card.Subtitle>
            <FaRegClock className="class-icn" /> {startTimeFormatted}-
            {endTimeFormatted} ({cls.number_of_sessions} session
            {cls.number_of_sessions > 1 ? "s" : ""})
          </Card.Subtitle>
          <Card.Subtitle>
            <FaLocationDot className="class-icn" /> {cls.address}{" "}
            {cls.postal}
          </Card.Subtitle>
          <Card.Subtitle>
            <IoLanguage className="class-icn" /> {langFormatted}
          </Card.Subtitle>
          <Card.Subtitle>
            <FaRegUser className="class-icn" /> {cls.trainer_name}
          </Card.Subtitle>
        </td>
        <td>
          <Button key={`${id}_${index}_edit_class_btn`} variant="primary" onClick={() => onEdit(cls.id)}>Edit</Button>
        </td>
        <td>
          <Button key={`${id}_${index}_delete_class_btn`} variant="danger" onClick={() => onDelete(cls.id)}>Delete</Button>
        </td>
      </tr>
    </Fragment>
  );
}

const Classes = ({ classes, course, onEdit, onDelete }) => {
  return (
    <Table responsive="sm">
      <tbody>
        <tr>
          <th>Class</th>
          <th>Edit</th>
          <th>Delete</th>
        </tr>
        {
          classes.length > 0
            ? classes.map(function (c, i) {
              return <Class
                key={`${course.id}_${c.id}_class_component`}
                id={course.id}
                cls={c}
                index={i}
                onEdit={onEdit}
                onDelete={onDelete} />
            })
            :
            <tr>
              <td style={{ padding: "50px 0px 25px 0px" }} colSpan={3}>
                Sorry, No classes found. Please add a new class by clicking the "Add New Class" button below.
              </td>
            </tr>
        }
      </tbody>
    </Table>
  )
}

const ClassesModal = ({
  onTriggerMessage,
  classes,
  trainers,
  locations,
  currentCourse,
  showFlag,
  isLoading,
  onFormClose,
  onClassAdd,
  onClassEdit,
  onClassDelete
}) => {
  const [currentClass, setCurrentClass] = useState(defaultClass);

  const [showClassFormFlag, setShowClassFormFlag] = useState(false);
  const showClassForm = () => setShowClassFormFlag(true);
  const closeClassForm = () => setShowClassFormFlag(false);

  const [showDeleteConfirmationFlag, setShowDeleteConfirmationFlag] = useState(false);
  const showDeleteConfirmation = () => setShowDeleteConfirmationFlag(true);
  const closeDeleteConfirmation = () => setShowDeleteConfirmationFlag(false);

  const courseName = currentCourse.course_translations.length > 0
    ? currentCourse.course_translations[0].name
    : "";
  const title = `${courseName} (${currentCourse.course_code})`;

  /*******************************************************************************************************
   *  ClassForm API functions
   ******************************************************************************************************/
  async function postClass(cls) {
    const onSuccess = (data) => {
      const rows = data.result.rows;
      const newClassId = rows[0].id;

      let updatedClass = { ...cls };
      updatedClass.id = newClassId;
      updatedClass.status = ObjectStatus.Sync;

      onClassAdd(cls);

      onTriggerMessage(
        "Success",
        "Successfully uploaded class to the server",
      );

      closeClassForm();
    };

    // Upload class details
    const submitObject = {
      course_id: cls.course_id,
      location_id: cls.location_id,
      start_date: cls.start_date,
      end_date: cls.end_date,
      trainer_id: cls.trainer_id,
      language: cls.language,
      number_of_sessions: cls.number_of_sessions,
    };

    await postDataWithHeaderHelper("/v1/classes/add", submitObject, onTriggerMessage, onSuccess);
  }

  async function postUpdateClass(cls) {
    const onSuccess = (_) => {

      let updatedClass = { ...cls };
      updatedClass.status = ObjectStatus.Sync;

      setCurrentClass(updatedClass);
      onClassEdit(updatedClass);
      onTriggerMessage(
        "Success",
        "Successfully updated class to the server",
      );

    };

    // Upload class details
    const submitObject = {
      id: cls.id,
      course_id: cls.course_id,
      location_id: cls.location_id,
      start_date: cls.start_date,
      end_date: cls.end_date,
      trainer_id: cls.trainer_id,
      language: cls.language,
      number_of_sessions: cls.number_of_sessions,
    };

    await postDataWithHeaderHelper("/v1/classes/update", submitObject, onTriggerMessage, onSuccess);
  }

  async function postDeleteClass(cls) {
    const onSuccess = (_) => {

      onTriggerMessage(
        "Success",
        "Successfully deleted class from the server",
      );

      closeDeleteConfirmation();
      onClassDelete(cls);
    };

    // Delete class
    const submitObject = {
      id: cls.id,
    };

    await postDataWithHeaderHelper("/v1/classes/delete",
      submitObject,
      onTriggerMessage,
      onSuccess);
  }

  /*******************************************************************************************************
   *  Classes with EditCourse functions
   ******************************************************************************************************/
  /**
   * We should submit the updated class to API and update EditCourses
   */
  async function onEdit(classForm) {
    await postUpdateClass(classForm);
  }

  /**
   * We should submit the new class to API and add to EditCourses
   */
  async function onAdd(classForm) {
    await postClass(classForm);
    setCurrentClass({ ...defaultClass });
  }

  /**
   * We should delete the class using API and remove from EditCourses
   */
  function onDeleteCourseConfirmation() {
    postDeleteClass({ ...currentClass });
    setCurrentClass({ ...defaultClass });
  }

  /*******************************************************************************************************
   *  ClassForm functions
   ******************************************************************************************************/
  function onDelete(id) {
    const index = classes.findIndex((c) => c.id == id);
    setCurrentClass(classes[index]);
    showDeleteConfirmation();
  }

  function onAddClass() {
    setCurrentClass({ ...defaultClass, course_id: currentCourse.id });
    showClassForm();
  }

  function onEditClass(id) {
    const index = classes.findIndex((c) => c.id == id);
    setCurrentClass(classes[index]);
    showClassForm();
  }

  return (
    <>
      <Modal size="lg" centered backdrop="static" keyboard={false} show={showFlag} onHide={onFormClose}>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoading
            ? <LoadingIcon />
            : <Classes
              classes={classes}
              course={currentCourse}
              onEdit={onEditClass}
              onDelete={onDelete} />
          }
        </Modal.Body >
        {/* User should be able to close the classes modal */}
        <Modal.Footer>
          <Button onClick={onAddClass}>Add New Class</Button>
          <Button variant="secondary" onClick={onFormClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal >
      {/* Display the delete confirmation below we allow deletion */}
      <DeleteConfirmation
        title={`${currentClass.trainer_name} at ${currentClass.location_name}`}
        description="Are you sure you want to delete this class?"
        showFlag={showDeleteConfirmationFlag}
        onConfirmation={onDeleteCourseConfirmation}
        onClose={closeDeleteConfirmation} />
      {/* Show the class form to update the class */}
      {showClassFormFlag
        ? <ClassForm
          locations={locations}
          trainers={trainers}
          currentClass={currentClass}
          showFlag={showClassFormFlag}
          onFormClose={closeClassForm}
          onCreate={onAdd}
          onUpdate={onEdit} />
        : <></>}
    </>
  );
};

export default ClassesModal;
